import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Key, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Authenticated password change page.
 * Two entry paths:
 *  1. Admin sets a temporary password for the member → user.must_change_password=true
 *     → user is force-routed here from MemberLogin and cannot proceed until they rotate.
 *  2. Voluntary self-service password change from the member profile (future).
 */
export default function ChangePassword() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const forced = !!user?.must_change_password;

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success('Password updated.');
        const access = user?.access;
        if (access === 'Administrator' || access === 'Admin' || access === 'Webmaster') {
          navigate('/admin/registrations');
        } else {
          navigate('/members');
        }
      } else {
        toast.error(data.detail || 'Could not update password.');
      }
    } catch (err) {
      toast.error('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key size={20} className="text-cafa-red" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          {forced && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2 items-start" data-testid="force-change-banner">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                You're using a temporary password issued by an administrator. Please choose a new password to continue using your account.
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="current">{forced ? 'Temporary password' : 'Current password'}</Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                data-testid="change-password-current"
              />
            </div>
            <div>
              <Label htmlFor="next">New password</Label>
              <Input
                id="next"
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                data-testid="change-password-new"
              />
              <p className="text-[11px] text-stone-500 mt-1">Minimum 8 characters.</p>
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="change-password-confirm"
              />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white" data-testid="change-password-submit">
              {submitting ? 'Updating...' : 'Update password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
